import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Layers, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, DeepPageDetails } from "@/types/technicalAudit";

interface DeepPageCardProps {
	issue: PageIssue;
}

function getDepthColor(depth: number): { bar: string; text: string } {
	if (depth <= 3) return { bar: "bg-green-500", text: "text-green-700 dark:text-green-400" };
	if (depth <= 5) return { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" };
	return { bar: "bg-red-500", text: "text-red-700 dark:text-red-400" };
}

export function DeepPageCard({ issue }: DeepPageCardProps) {
	const details = issue.issue_details as DeepPageDetails;
	const colors = getDepthColor(details.click_depth);
	const maxDisplay = Math.max(details.max_depth * 2, details.click_depth + 2);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Deep Page</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This page is {details.click_depth} clicks from the homepage. Pages deeper than {details.max_depth} clicks get less crawl priority and link equity.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Layers className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Crawl Depth
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className={cn("text-3xl font-bold", colors.text)}>
							{details.click_depth}
						</span>
						<span className="text-sm text-muted-foreground">
							Max recommended: {details.max_depth}
						</span>
					</div>

					{/* Depth indicator as steps */}
					<div className="flex items-center gap-1">
						{Array.from({ length: maxDisplay }).map((_, i) => (
							<div
								key={i}
								className={cn(
									"h-6 flex-1 rounded",
									i < details.click_depth
										? i < details.max_depth
											? "bg-amber-300"
											: "bg-red-400"
										: "bg-muted",
								)}
							/>
						))}
					</div>
					<div className="flex items-center justify-between text-xs text-muted-foreground">
						<span>Homepage</span>
						<span>Depth {maxDisplay}</span>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
